import { TabConfig, SECTION_TYPE, dynamicComponentSchema, DisplayTranformation, DynamicFormArray, FIELD_TYPES } from './form.model';
import { DynamicCardsComponent, DynamicCardSchemaItem } from '../components/dynamic-cards/dynamic-cards.component';
import { AreaDropdown, CalendarYearDropdown, TotalAmountText, TotalCountText } from './form-fields.model';
import { FilterListContainerComponent } from '../layout/filter-list-container/filter-list-container.component';
import { FormContainerComponent } from '../layout/form/form-container/form-container.component';
import { DashboardContainerComponent } from '../layout/dashboard-container/dashboard-container.component';
import { ChartComponent } from '../layout/chart/chart.component';
import { DsMapDrilldownComponent } from '../components/dealer-scorecard-content/ds-map-drilldown/ds-map-drilldown.component';
import { DataTableSchema, DefaultTableSchema, TITLE_TYPE } from './dynamic-data-table.model';
import { ColumnChartModel, CommonChartModel, CHART_TYPE, DonutChartModel, PieChartModel, BarChartModel, AreasplineChartModel } from './chart.model';
import { ProgressBarItem, DynamicProgressBarComponent } from '../components/dynamic-progress-bar/dynamic-progress-bar.component';
import { ApiEnvNames, FETCH_METHODS, SearchConfig } from './api.model';

const CERTIFIABLE_ROLES = `Certifiable Roles`;
const NON_CERTIFIABLE_ROLES = `Non-Certifiable Roles`;
const TOP_DEALER_LABEL = `Top Dealer`;
const ROLE_LABELS: Record<string, string> = { CERTIFIABLE: CERTIFIABLE_ROLES, NON_CERTIFIABLE: NON_CERTIFIABLE_ROLES };
const roleLabel = (name: string): string => ROLE_LABELS[name] ?? name;
const LEXUS_PRODUCT_LINE_LABEL = `Lexus product line`;
const TOTAL_ASSOCIATES_LABEL = `Total Associates`;
const TOTAL_SALES_LABEL = `Total Sales`;
const UNITS_SOLD_LABEL = `Units Sold`;
const SALES_UNITS_LABEL = `Sales Units`;
const TOTAL_SALES = `Total Sales`;
const TOTAL_COUNT = `Total Count`;
const Dealer_Details = `Dealer Details`;
const Dealer_Name = `Dealer Name`;
const Dealer_Code = `Dealer Code`;
const ASSOCIATE_COUNT_AXIS_TITLE = `Associate Count`;
const ASSOCIATES_TOOLTIP_HEADER = `<span style="font-size:12px"><b>{point.name}</b></span><br>`;
const generateDealerYearOptions = (): Array<{ fieldLabel: number; fieldValue: string }> => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = 2024; year <= currentYear; year++) {
        years.push({ fieldLabel: year, fieldValue: year.toString() });
    }
    return years;
};
const dealerYearOptions = generateDealerYearOptions();
const SMALL_POINT_NAME_TOOLTIP_HEADER = `<span style="font-size:11px">{point.name}</span><br>`;
const ASSOCIATES_TOOLTIP_HEADER_SLASH = `<span style="font-size:12px"><b>{point.name}</b></span><br/>`;
const DEALER_SALES_TOOLTIP_POINT = '<span style="font-size:11px"><b>Dealer Name:</b> {point.dealerName}</span><br><span style="font-size:11px"><b>Dealer Code:</b> {point.name}</span><br><span style="font-size:11px"><b>Total Sales:</b> {point.y}</span><br/>';
const MODEL_UNITS_SOLD_TOOLTIP_POINT = '<span style="font-size:11px"><b>Model:</b> {point.name}</span><br><span style="font-size:11px"><b>Units Sold:</b> {point.y}</span><br/>';

export const DealerScorecardDashboardTitle = 'Dealer Analytics Dashboard';

export const DealerScorecardSectionNames = {
    DashboardDealerSelector: 'dealerScorecardDashboardDealerSelector',
    DashboardCards: 'dealerScorecardDashboardCards',
    DashboardSalesTrendChart: 'dealerScorecardDashboardSalesTrendChart',
    DashboardRoleMixChart: 'dealerScorecardDashboardRoleMixChart',
    DashboardTopModelsChart: 'dealerScorecardDashboardTopModelsChart',
    SummaryCards: 'dealerScorecardSummary',
    DealerList: 'dealerScorecardList',
    CertificationProgress: 'dealerScorecardCertificationProgress',
    CertificationChart: 'dealerScorecardCertificationChart',
    AssociatesChart: 'dealerScorecardAssociates',
    RoleMixChart: 'dealerScorecardRoleMix',
    SalesOverviewCards: 'salesOverviewCards',
    SalesByDealerChart: 'salesByDealerChart',
    Top10DealersChart: 'top10DealersChart',
    SalesTrendAreasplineChart: 'salesTrendAreasplineChart',
} as const;

export const DealerScorecardCardsSchema: DynamicCardSchemaItem[] = [
    { label: TOTAL_ASSOCIATES_LABEL, subLabel: 'Across 8 of 8 Dealers', fieldKey: 'associate_count' },
    { label: 'Certifiable Associates', subLabel: 'Eligible for certification', fieldKey: 'certifiable_count', subtitleFieldKey: 'non_certifiable_count' },
    { label: 'Average Tenure', subLabel: 'Weighted by headcount', fieldKey: 'avg_brand_tenure' },
    { label: 'Training Hours Completed', subLabel: '25.5 hrs / Associate', fieldKey: 'training_hours' },
];

export const SalesOverviewFilterSchema: DynamicFormArray = {
    FieldsArray: [{
        fieldName: 'event_name',
        displayName: 'Event',
        fieldType: FIELD_TYPES.DROPDOWN,
        defaultValue: '',
        selectedValue: '',
        disabled: false,
        isAutoComplete: false,
        isLoading: true,
        displayTranformation: DisplayTranformation.none,
        showSelect: false,
        options: [],
    }],
    ButtonsArray: [],
};

export const DealerPerformanceDashboardDealerFilterSchema: DynamicFormArray = {
    formName: 'dealerPerformanceDashboardDealerFilter',
    FieldsArray: [
        {
            fieldName: 'dealerId',
            displayName: 'Dealer',
            fieldType: FIELD_TYPES.DROPDOWN,
            defaultValue: '',
            selectedValue: '',
            disabled: false,
            isAutoComplete: true,
            showSelect: true,
            required: true,
            isLoading: true,
            options: [],
        },
        {
            ...structuredClone(CalendarYearDropdown),
            options: dealerYearOptions,
            displayName: 'Year',
        }
    ],
    ButtonsArray: [],
};

export const DealerPerformanceDashboardCardsSchema: DynamicCardSchemaItem[] = [
    { label: ASSOCIATE_COUNT_AXIS_TITLE, subLabel: 'Across dealer network', fieldKey: 'associate_count', icon: 'fa-solid fa-users' },
    { label: TOTAL_SALES_LABEL, subLabel: 'All dealers combined', fieldKey: 'total_sales_count', icon: 'fa-solid fa-tag' },
    { label: 'Model Count', subLabel: LEXUS_PRODUCT_LINE_LABEL, fieldKey: 'model_count', icon: 'fa-solid fa-car' },
    { label: 'Certifiable Count', subLabel: 'Eligible associates', fieldKey: 'certifiable_count', icon: 'fa-solid fa-certificate' },
    { label: 'Non-Certifiable Count', subLabel: 'Non-certifiable roles', fieldKey: 'non_certifiable_count', icon: 'fa-solid fa-ban' },
    { label: 'Training Hours Spent', subLabel: 'Completed hours', fieldKey: 'training_hours', icon: 'fa-solid fa-graduation-cap' },
];

// All table column schemas
export const DealerScorecardTableSchema: DataTableSchema = {
    ...structuredClone(DefaultTableSchema),
    columnSchema: [
        { label: Dealer_Code, fieldKey: 'dealerCode' },
        { label: Dealer_Name, fieldKey: 'dealerName' },
        { label: 'Area', fieldKey: 'region_description' },
        { label: 'Associates', fieldKey: 'associate_count' },
        { label: 'Certifiable', fieldKey: 'certifiable_count' },
        { label: 'Non-Cert', fieldKey: 'non_certifiable_count' },
        { label: 'Certified', fieldKey: 'certified_count' },
        { label: 'Certification %', fieldKey: 'certification_percentage', isPercent: true },
        { label: 'Avg Tenure', fieldKey: 'avg_dealer_tenure' },
        { label: 'Training Hrs', fieldKey: 'trainingHours' },
    ],
    dataSource: [],
    isExportRequired: true,
    tableTitle: 'Dealer breakdown',
    exportConfig: {
        fileName: 'Dealer_Breakdown',
        exportMessage: 'Dealer Breakdown',
        schema: {
            dealerCode: Dealer_Code,
            dealerName: Dealer_Name,
            region_description: 'Area',
            associate_count: 'Associates',
            certifiable_count: 'Certifiable',
            non_certifiable_count: 'Non-Cert',
            certified_count: 'Certified',
            certification_percentage: 'Certification %',
            avg_dealer_tenure: 'Avg Tenure',
            trainingHours: 'Training Hrs',
        },
    },
};

const SalesOverviewColumns = [
    { label: Dealer_Code, fieldKey: 'dealer_code' },
    { label: 'Number of associates attended the ES training', fieldKey: 'associate_count' },
    { label: 'Sales before training (only ES model cars)', fieldKey: 'sales_before_event' },
    { label: 'Sales after training (only ES model cars)', fieldKey: 'sales_after_event' },
];

export const SalesOverviewTableSchema: DataTableSchema = {
    ...structuredClone(DefaultTableSchema),
    tableTitle: 'Sales Data',
    columnSchema: SalesOverviewColumns,
    dataSource: [],
    inputFilter: '',
    displayedColumns: [],
    isPaginationRequired: true,
    isSortRequired: true,
    isSearchRequired: false,
    isExportRequired: true,
    isFilterListExportRequired: false,
    exportConfig: {
        fileName: 'Sales_Data',
        exportMessage: 'Sales Data',
        schema: Object.fromEntries(SalesOverviewColumns.map((col) => [col.fieldKey, col.label])),
    },
};
// All chart schemas 
export const DealerAssociatesChart: CommonChartModel = {
    ...structuredClone(ColumnChartModel),
    titleSchema: {
        label: '',
        title: 'Total Associates by Area',
        type: TITLE_TYPE['Static'],
    },
    xAxis: {
        type: 'category',
        title: { text: 'Area' },
    },
    yAxis: {
        title: { text: TOTAL_ASSOCIATES_LABEL },
        min: 0,
    },
    legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: { fontSize: '12px' },
    },
    tooltip: {
        headerFormat: ASSOCIATES_TOOLTIP_HEADER,
        pointFormat: '<span style="color:{point.color}">Associates</span>: <b>{point.y}</b><br/>'
    },
    series: [{
        name: 'Associates by Area',
        type: CHART_TYPE['ColumnChart'],
        data: [],
        dataLabels: { enabled: true, format: '{point.y}' },
    }],
    plotOptions: {
        column: {
            colorByPoint: true,
            dataLabels: { enabled: false },
            pointPadding: 0.2,
            borderWidth: 0,
        }
    },
    exportConfig: {
        fileName: 'Dealer_Associates_By_Area',
        schema: {
            'area': 'Area',
            'totalAssociates': 'Total Associates',
        },
        groupedSheets: {
            useResponseKeys: true,
            mainSheetName: 'Area Summary',
            schemaMap: {
                'dealers': {
                    sheetName: Dealer_Details,
                    schema: {
                        'dlrCode': Dealer_Code,
                        'name': Dealer_Name,
                        'region': 'Area',
                        'state': 'State',
                        'totalAssociates': 'Total Associates',
                        'certifiable': 'Certifiable',
                        'nonCertifiable': 'Non-Cert.',
                        'certified': 'Certified',
                        'master': 'Master',
                        'senior': 'Senior',
                        'foundation': 'Foundation',
                    },
                },
            },
        },
    },
    mapDrilldownConfig: {
        tableColumns: [
            { label: Dealer_Code, fieldKey: 'dlrCode', className: 'dealer-code' },
            { label: Dealer_Name, fieldKey: 'name', className: 'dealer-name' },
            { label: 'Area', fieldKey: 'region', valueType: 'regionBadge' },
            { label: TOTAL_ASSOCIATES_LABEL, fieldKey: 'totalAssociates', valueType: 'number', className: 'num associates' },
            { label: 'Certifiable', fieldKey: 'certifiable', valueType: 'number', className: 'num' },
            { label: 'Non-Cert.', fieldKey: 'nonCertifiable', valueType: 'number', className: 'num' },
            { label: 'Certified', fieldKey: 'certified', valueType: 'number', className: 'num' },
            { label: 'Master', fieldKey: 'master', valueType: 'number', className: 'num' },
            { label: 'Senior', fieldKey: 'senior', valueType: 'number', className: 'num' },
            { label: 'Foundation', fieldKey: 'foundation', valueType: 'number', className: 'num' },
        ],
        areaValueFieldKey: 'totalAssociates',
        stateValueFieldKey: 'totalAssociates',
        sortFieldKey: 'totalAssociates',
        areaMetricLabel: 'Total Associates By Area',
        stateMetricLabel: 'State Associates',
        footerMetricLabel: TOTAL_ASSOCIATES_LABEL,
        footerMetricFieldKey: 'totalAssociates',
        valueType: 'number',
    },
    dataSchema: {
        label: 'region',
        value: 'totalAssociates',
        drillDownConfig: {
            label: 'name',
            value: 'totalAssociates',
            type: CHART_TYPE['ColumnChart'],
            drillDownField: 'dealers',
            xAxis: { title: 'Dealers' },
            yAxis: { title: TOTAL_ASSOCIATES_LABEL },
            tooltip: {
                headerFormat: ASSOCIATES_TOOLTIP_HEADER,
                pointFormat: '<span style="font-size:11px"><b>Dealer Name:</b> {point.dealerName}</span><br><span style="font-size:11px"><b>Total Associates:</b> {point.y}</span><br/>',
            },
        },
    },
};

export const DealerRoleMixChart: CommonChartModel = {
    ...structuredClone(DonutChartModel),
    titleSchema: {
        label: '',
        title: 'Role Mix',
        type: TITLE_TYPE['Static'],
    },
    tooltip: {
        useHTML: true,
        formatter: function (this: any) {
            return `<span style="font-size:11px"><b>${roleLabel(this.point.name)}</b></span><br>` +
                `<span style="color:${this.point.color}">Total Associates</span>: <b>${this.y}</b><br/>`;
        },
    },
    legend: {
        ...DonutChartModel.legend,
        labelFormatter: function (this: any) { return roleLabel(this.name); },
    },
    series: [{
        name: 'Role Mix',
        type: CHART_TYPE['DonutChart'],
        data: [],
        dataLabels: { enabled: true },
    }],
    plotOptions: {
        pie: {
            innerSize: '70%',
            size: '115%',
            allowPointSelect: true,
            cursor: 'pointer',
            showInLegend: true,
            colors: ['#35aef4', '#5750c6'],
            dataLabels: {
                enabled: true,
                crop: false,
                overflow: 'allow',
                formatter: function (this: any) { return `<b>${roleLabel(this.point.name)}</b><br>${this.y}`; },
            },
        }
    },
    exportConfig: {
        fileName: 'Dealer_Role_Mix',
        schema: {
            'associate_type': 'Role Type',
            'total_associate_count': 'Total Associate Count',
        },
        groupedSheets: {
            useResponseKeys: true,
            mainSheetName: 'Role Mix Summary',
            schemaMap: {
                'dealer_breakdown': {
                    sheetName: Dealer_Details,
                    schema: {
                        'dealer_code': Dealer_Code,
                        'dealer_name': Dealer_Name,
                        'associate_count': 'Associate Count',
                    },
                },
            },
        },
    },
    dataSchema: {
        label: 'associate_type',
        value: 'total_associate_count',
        drillDownConfig: {
            label: 'dealer_code',
            value: 'associate_count',
            type: CHART_TYPE['ColumnChart'],
            drillDownField: 'dealer_breakdown',
            xAxis: { title: 'Dealers' },
            yAxis: { title: 'Count', min: 0 },
            tooltip: {
                useHTML: true,
                formatter: function (this: any) {
                    const point = this.point || {};
                    const dealerName = point.dataset?.dealer_name ?? point.dealer_name ?? '-';
                    return `<span style="font-size:12px"><b>${point.name}</b></span><br>` +
                        `<span style="font-size:11px"><b>Dealer Code:</b> ${point.name}</span><br>` +
                        `<span style="font-size:11px"><b>Dealer Name:</b> ${dealerName}</span><br>` +
                        `<span style="font-size:11px"><b>Count:</b> ${point.y}</span><br/>`;
                },
            },
        },
    },
};

export const DealerCertificationPieChart: CommonChartModel = {
    ...structuredClone(PieChartModel),
    titleSchema: {
        label: '',
        title: '',
        type: TITLE_TYPE['Static'],
    },
    tooltip: {
        headerFormat: SMALL_POINT_NAME_TOOLTIP_HEADER,
        pointFormat: '<span style="color:{point.color}">Count</span>: <b>{point.y}</b><br/>'
    },
    series: [{
        name: 'Certification Status',
        type: CHART_TYPE['PieChart'],
        data: [],
        dataLabels: { enabled: true, format: '<b>{point.name}</b> ({point.y})' },
    }],
    exportConfig: {
        fileName: 'Dealer_Certification_Status',
        schema: {
            'cert_type': 'Certification Type',
            'total_associate_count': 'Total Associate Count',
        },
        groupedSheets: {
            useResponseKeys: true,
            mainSheetName: 'Certification Summary',
            schemaMap: {
                'dealer_breakdown': {
                    sheetName: Dealer_Details,
                    schema: {
                        'dealer_code': Dealer_Code,
                        'associate_count': 'Associate Count',
                    },
                },
            },
        },
    },
    dataSchema: {
        label: 'cert_type',
        value: 'total_associate_count',
        drillDownConfig: {
            label: 'dealer_code',
            value: 'associate_count',
            type: CHART_TYPE['ColumnChart'],
            drillDownField: 'dealer_breakdown',
            xAxis: { title: 'Dealers' },
            yAxis: { title: ASSOCIATE_COUNT_AXIS_TITLE },
        },
    },
};

export const SalesByDealerChart: CommonChartModel = {
    ...structuredClone(ColumnChartModel),
    titleSchema: {
        label: '',
        title: 'Total Sales by Area',
        type: TITLE_TYPE['Static'],
    },
    tooltip: {
        headerFormat: SMALL_POINT_NAME_TOOLTIP_HEADER,
        pointFormat: '<span style="color:{point.color}">Sales</span>: <b>{point.y}</b><br/>'
    },
    xAxis: {
        type: 'category',
        title: { text: 'Area' },
    },
    yAxis: {
        title: { text: 'Sales' },
        min: 0,
        allowDecimals: false,
    },
    legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: { fontSize: '12px' },
    },
    series: [{
        name: 'Sales by Area',
        type: CHART_TYPE['ColumnChart'],
        data: [],
        dataLabels: { enabled: false },
    }],
    plotOptions: {
        column: {
            colorByPoint: true,
            dataLabels: { enabled: false },
            pointPadding: 0.2,
            borderWidth: 0,
        }
    },
    exportConfig: {
        fileName: 'Sales_By_Area',
        schema: {
            'area': 'Area',
            'total_sales': TOTAL_SALES,
        },
        groupedSheets: {
            useResponseKeys: false,
            dynamicSheets: true,
            mainSheetName: 'Sales By Area Summary',
            sheetKeyField: 'area',
            sheetDataField: 'dealers',
            sheetSchema: {
                'area': 'Area',
                'dealerCode': Dealer_Code,
                'dealerName': Dealer_Name,
                'totalSales': TOTAL_SALES,
            },
        },
    },
    mapDrilldownConfig: {
        tableColumns: [
            { label: Dealer_Name, fieldKey: 'name', className: 'dealer-name' },
            { label: 'Area', fieldKey: 'region', valueType: 'regionBadge' },
            { label: 'Sales Revenue', fieldKey: 'salesRevenue', valueType: 'currencyMillions', className: 'num revenue highlight-revenue' },
        ],
        areaValueFieldKey: 'salesTotal',
        stateValueFieldKey: 'salesRevenue',
        sortFieldKey: 'salesRevenue',
        areaMetricLabel: 'Total Sales By Area',
        stateMetricLabel: 'State Sales Revenue',
        footerMetricLabel: TOTAL_SALES_LABEL,
        footerMetricFieldKey: 'salesRevenue',
        valueType: 'currencyMillions',
    },
    dataSchema: {
        label: 'area',
        value: 'total_sales',
        drillDownConfig: {
            label: 'dealerCode',
            value: 'totalSales',
            type: CHART_TYPE['ColumnChart'],
            drillDownField: 'dealers',
            xAxis: { title: 'Dealers' },
            yAxis: {
                title: TOTAL_SALES_LABEL,
                min: 0,
                allowDecimals: false,
                tickInterval: 50,
            },
            tooltip: {
                headerFormat: ASSOCIATES_TOOLTIP_HEADER,
                pointFormat: DEALER_SALES_TOOLTIP_POINT,
            },
            dataLabels: { enabled: false },
            drillDownConfig: {
                label: 'model',
                value: 'sales',
                type: CHART_TYPE['ColumnChart'],
                drillDownField: 'models',
                xAxis: { title: 'Model' },
                yAxis: {
                    title: UNITS_SOLD_LABEL,
                    min: 0,
                    allowDecimals: false,
                },
                tooltip: {
                    headerFormat: ASSOCIATES_TOOLTIP_HEADER,
                    pointFormat: MODEL_UNITS_SOLD_TOOLTIP_POINT,
                },
                dataLabels: { enabled: false },
            },
        },
    },
};

export const Top10DealersChart: CommonChartModel = {
    ...structuredClone(ColumnChartModel),
    titleSchema: {
        label: '',
        title: 'Top 10 Dealers by Sales',
        type: TITLE_TYPE['Static'],
    },
    xAxis: {
        type: 'category',
        title: { text: 'Dealers' },
        labels: {
            style: {
                fontSize: '11px',
            },
        },
    },
    yAxis: {
        title: { text: SALES_UNITS_LABEL },
        type: 'linear',
    },
    tooltip: {
        useHTML: true,
        formatter: function (this: any) {
            const point = this.point || {};
            const dataset = point.dataset || {};
            const primaryData = point.primaryData || {};
            const dealerCode = primaryData.dealer_code ?? point.dealerCode ?? dataset.dealer_code ?? point.name ?? '-';
            const dealerName = primaryData.dealer_name ?? point.dealerName ?? dataset.dealer_name ?? '-';
            const isDrilldownPoint = !!point.primaryData;
            const metricLabel = isDrilldownPoint ? 'Sales' : (this.series?.name || 'Sales');
            const modelName = isDrilldownPoint ? (point.name ?? '-') : '';
            const sales = point.y ?? 0;

            if (isDrilldownPoint) {
                return `<span style="font-size:12px"><b>Model:</b> ${modelName}</span><br>` +
                    `<span style="font-size:11px"><b>Dealer Name:</b> ${dealerName}</span><br>` +
                    `<span style="font-size:11px"><b>Dealer Code:</b> ${dealerCode}</span><br>` +
                    `<span style="color:${this.series?.color}"><b>${metricLabel}</b></span>: <b>${sales}</b> units<br/>`;
            }

            return `<span style="font-size:12px"><b>${dealerCode}</b></span><br>` +
                `<span style="font-size:11px"><b>Dealer Name:</b> ${dealerName}</span><br>` +
                `<span style="font-size:11px"><b>Dealer Code:</b> ${dealerCode}</span><br>` +
                `<span style="color:${this.series?.color}"><b>${metricLabel}</b></span>: <b>${sales}</b> units<br/>`;
        }
    },
    series: [{
        name: 'Sales',
        type: CHART_TYPE['ColumnChart'],
        data: [],
        dataLabels: { enabled: false, format: '{point.y}' },
        color: '#FF6B6B',
    }],
    plotOptions: {
        column: {
            dataLabels: { enabled: false },
            pointPadding: 0.2,
            borderWidth: 0,
        }
    },
    exportConfig: {
        fileName: 'Top_10_Dealers_by_Sales',
        schema: {
            'dealer_code': Dealer_Code,
            'dealer_name': Dealer_Name,
            'sales': 'Sales',
        },
        groupedSheets: {
            useResponseKeys: false,
            dynamicSheets: true,
            mainSheetName: 'Dealer Summary',
            sheetKeyField: 'dealer_code',
            sheetDataField: 'models',
            sheetSchema: {
                'model': 'Model',
                'sales': 'Sales',
            },
        },
    },
    dataSchema: {
        label: 'dealer_code',
        value: 'sales',
        drillDownConfig: {
            label: 'model',
            value: 'sales',
            type: CHART_TYPE['ColumnChart'],
            drillDownField: 'models',
            xAxis: { title: 'Model' },
            yAxis: {
                title: UNITS_SOLD_LABEL,
                min: 0,
                allowDecimals: false,
            },
            tooltip: {
                useHTML: true,
                formatter: function (this: any) {
                    const point = this.point || {};
                    const dealer = point.primaryData || {};
                    const dealerCode = dealer.dealer_code ?? '-';
                    const dealerName = dealer.dealer_name ?? '-';
                    const modelName = point.name ?? '-';
                    const units = point.y ?? 0;

                    return `<span style="font-size:12px"><b>Model:</b> ${modelName}</span><br>` +
                        `<span style="font-size:11px"><b>Dealer Code:</b> ${dealerCode}</span><br>` +
                        `<span style="font-size:11px"><b>Dealer Name:</b> ${dealerName}</span><br>` +
                        `<span style="color:${this.series?.color}"><b>Units Sold:</b></span> <b>${units}</b><br/>`;
                },
            },
            dataLabels: { enabled: false },
        },
    },
};

export const SalesTrendAreasplineChart: CommonChartModel = {
    ...structuredClone(AreasplineChartModel),
    titleSchema: {
        label: '',
        title: 'Sales Trend Across All Dealers',
        type: TITLE_TYPE['Static'],
    },
    xAxis: {
        title: { text: 'Dealers' },
        type: 'category',
        labels: {
            style: {
                fontSize: '9px',
            },
        },
    },
    yAxis: {
        title: { text: SALES_UNITS_LABEL },
        min: 0,
    },
    tooltip: {
        useHTML: true,
        formatter: function (this: any) {
            const point = this.point || {};
            const dealerCode = point.dataset?.dealer_code ?? point.name ?? '-';
            const dealerName = point.dataset?.dealer_name ?? '-';
            const sales = point.y ?? 0;

            return `<span style="font-size:12px"><b>${dealerCode}</b></span><br/>` +
                `<span style="font-size:11px"><b>Dealer Name:</b> ${dealerName}</span><br/>` +
                `<span style="font-size:11px"><b>Dealer Code:</b> ${dealerCode}</span><br/>` +
                `<span style="color:${this.series?.color}"><b>Sales</b></span>: <b>${sales}</b><br/>`;
        },
    },
    series: [{
        name: 'Sales Trend',
        type: CHART_TYPE['AreasplineChart'],
        data: [],
        dataLabels: { enabled: false },
        color: '#4ECDC4',
    }],
    plotOptions: {
        areaspline: {
            fillOpacity: 0.4,
        }
    },
    exportConfig: {
        fileName: 'Sales_Trend_Across_All_Dealers'
    },
    dataSchema: {
        label: 'dealer_code',
        value: 'sales',
    },
};

export const DealerPerformanceSalesRevenueTrendChart: CommonChartModel = {
    ...structuredClone(ColumnChartModel),
    isLineChart: true,
    titleSchema: {
        label: '',
        title: 'Sales Trend',
        type: TITLE_TYPE['Static'],
    },
    xAxis: {
        type: 'category',
        title: { text: 'Month' },
    },
    yAxis: {
        title: { text: 'Sales' },
        min: 0,
    },
    legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'right',
        verticalAlign: 'top',
    },
    tooltip: {
        useHTML: true,
        formatter: function (this: any) {
            return `<span style="font-size:12px"><b>${this.point?.name ?? ''}</b></span><br>` +
                `<span style="color:${this.series?.color}">${this.series?.name ?? 'Sales'}</span>: <b>${this.y ?? 0}</b><br/>`;
        },
    },
    series: [
        {
            name: 'Sales',
            type: CHART_TYPE['AreasplineChart'],
            color: '#e60028',
            data: [],
            dataLabels: {
                enabled: true,
                format: '{point.y}',
                y: -8,
                style: {
                    fontSize: '11px',
                    fontWeight: 'bold',
                    color: '#1f2937',
                    textOutline: 'none',
                },
            },
        },
    ],
    plotOptions: {
        areaspline: {
            fillOpacity: 0.28,
        },
        spline: {
            marker: {
                enabled: true,
            },
        },
    },
    exportConfig: {
        fileName: 'Sales_Trend',
    },
    dataSchema: {
        label: 'month_name',
        value: 'sales',
        tooltipLabelInfo: {
            sales: 'Sales'
        },
    },
};

export const DealerPerformanceCertificationDonutChart: CommonChartModel = {
    ...structuredClone(DonutChartModel),
    titleSchema: {
        label: '',
        title: 'Certifiable vs Non-Certifiable',
        type: TITLE_TYPE['Static'],
    },
    tooltip: {
        headerFormat: ASSOCIATES_TOOLTIP_HEADER_SLASH,
        pointFormat: '<span style="color:{point.color}">Associate Count</span>: <b>{point.y}</b><br/>',
    },
    yAxis: {
        title: { text: 'Count' },
        min: 0,
        allowDecimals: false,
        tickAmount: 6,
        tickPixelInterval: 40,
    },
    series: [{
        name: 'Certification',
        type: CHART_TYPE['DonutChart'],
        data: [],
        dataLabels: { enabled: true, format: '<b>{point.name}</b>: {point.y}' },
    }],
    plotOptions: {
        pie: {
            innerSize: '70%',
            allowPointSelect: true,
            cursor: 'pointer',
            showInLegend: true,
            dataLabels: { enabled: true, format: '<b>{point.name}</b>: {point.y}' },
        },
    },
    drilldown: {
        breadcrumbs: {
            position: {
                align: 'right'
            }
        },
        series: []
    },
    exportConfig: {
        fileName: 'Dealer_Performance_Certifiable_Non_Certifiable',
    },
    dataSchema: {
        label: 'status',
        value: 'count',
        drillDownConfig: {
            label: 'certification',
            value: 'count',
            type: CHART_TYPE['ColumnChart'],
            drillDownField: 'drilldownRows',
            xAxis: { title: 'Certification' },
            yAxis: { title: 'Count', min: 0, allowDecimals: false, tickAmount: 6, tickPixelInterval: 40 },
            tooltip: {
                useHTML: true,
                formatter: function (this: any) {
                    const point = this.point || {};
                    return `<span style="font-size:12px"><b>${point.name}</b></span><br>` +
                        `<span style="color:${this.series?.color}"><b>Count:</b></span> <b>${point.y ?? 0}</b><br/>`;
                },
            },
            dataLabels: { enabled: false },
        },
    },
};

export const DealerPerformanceTopModelsBarChart: CommonChartModel = {
    ...structuredClone(BarChartModel),
    titleSchema: {
        label: '',
        title: 'Top Models by Sales',
        type: TITLE_TYPE['Static'],
    },
    xAxis: {
        title: { text: 'Model' },
        type: 'category',
    },
    yAxis: {
        title: { text: SALES_UNITS_LABEL },
        min: 0,
        allowDecimals: false,
    },
    tooltip: {
        headerFormat: ASSOCIATES_TOOLTIP_HEADER_SLASH,
        pointFormat: '<span style="color:{point.color}">Sales</span>: <b>{point.y}</b> units<br/>',
    },
    series: [{
        name: 'Sales',
        type: CHART_TYPE['BarChart'],
        data: [],
        dataLabels: { enabled: false, format: '{point.y}' },
        color: '#e60028',
    }],
    plotOptions: {
        bar: {
            colorByPoint: false,
            dataLabels: { enabled: false },
            pointPadding: 0.2,
            borderWidth: 0,
        },
    },
    exportConfig: {
        fileName: 'Top_Models_By_Sales',
    },
    dataSchema: {
        label: 'model',
        value: 'sales',
    },
};


// All scorecard API endpoints live here
const scorecardApi = (path: string, body?: SearchConfig['body']): SearchConfig => ({
    method: FETCH_METHODS['Get'],
    path,
    urlPath: ApiEnvNames['DealerScorecard'],
    ...(body && { body }),
});
const areaYearBody = () => ({ area: '', year: '' });
const areaBody = () => ({ area: '' });

export const ScorecardDashboardSummarySearchConfig = scorecardApi('getScorecardDashboardSummary');
export const ScorecardSalesTrendPerMonthSearchConfig = scorecardApi('salesTrendperMonth');
export const ScorecardTopModelsBySalesSearchConfig = scorecardApi('topModelsBySales');
export const ScorecardCertificationSummarySearchConfig = scorecardApi('getCertificationSummary');
export const DealerBreakdownSearchConfig = scorecardApi('dealerBreakdown');
export const CertificationStatusDealerBreakdownSearchConfig = scorecardApi('certificationStatusDealerBreakdown', areaBody());
export const CertifiableRoleAssociatesSearchConfig = scorecardApi('certifiableRoleAssociates', areaBody());
export const AssociateByAreaAndStateSearchConfig = scorecardApi('associateByAreaAndState');
export const TopTenDealersBySalesSearchConfig = scorecardApi('topTenDealersbysales', areaYearBody());
export const TotalSalesByAreaSearchConfig = scorecardApi('totalSalesbyArea', areaYearBody());
export const SalesTrendsAcrossAllDealersSearchConfig = scorecardApi('salesTrendsAcrossAllDealers', areaYearBody());
export const SalesOverviewCardsSearchConfig = scorecardApi('salesOverviewSummary', areaYearBody());
export const SalesDataBeforeAfterEventSearchConfig = scorecardApi('salesDataBeforeAfterEvent', { event_name: '', year: '' });
export const DealerPerformanceDashboardFilterTrigger = 'dealerPerformanceDashboardFilter';
export const DealerPerformanceDashboardRequiredPayloadFields: string[] = [];
export const DealerOverviewFilterTrigger = 'dealerOverviewFilter';
export const SalesOverviewFilterTrigger = 'salesOverviewFilter';

export const transformScorecardCertificationSummary = (rows: any[]): any[] => {
    const summary = rows?.[0] ?? {};
    const certifiedCount = Number(summary?.certified_count ?? summary?.certifiedCount ?? 0);
    const nonCertifiedCount = Number(summary?.non_certified_count ?? summary?.nonCertifiedCount ?? 0);
    const masterCount = Number(summary?.master ?? 0);
    const certifiedLevelCount = Number(summary?.certified ?? 0);
    const seniorCount = Number(summary?.senior ?? 0);
    const foundationCount = Number(summary?.foundation ?? 0);
    if (!rows?.length || [certifiedCount, nonCertifiedCount, masterCount, certifiedLevelCount, seniorCount, foundationCount].every((value) => value === 0)) {
        return [];
    }
    return [
        {
            status: 'Certified',
            count: certifiedCount,
            color: '#35b66f',
            drilldownRows: [
                { certification: 'Master', count: masterCount },
                { certification: 'Certified', count: certifiedLevelCount },
                { certification: 'Senior', count: seniorCount },
                { certification: 'Foundation', count: foundationCount },
            ],
        },
        {
            status: 'Non-Certified',
            count: nonCertifiedCount,
            color: '#7f878f',
            drilldownRows: [
                { certification: 'Non-Certified', count: nonCertifiedCount },
            ],
        },
    ];
};

const formatCertificationTypeLabel = (certType: string): string => `${certType}`
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const transformCertificationStatusProgressBars = (rows: any[]): ProgressBarItem[] => {
    if (!Array.isArray(rows)) {
        return [];
    }

    const totalAssociates = rows.reduce((sum, row) => sum + Number(row?.total_associate_count ?? row?.totalAssociateCount ?? 0), 0);
    return rows.map((row, index): ProgressBarItem => {
        const count = Number(row?.total_associate_count ?? row?.totalAssociateCount ?? 0);
        return {
            label: formatCertificationTypeLabel(row?.cert_type ?? row?.certType ?? ''),
            count,
            pct: totalAssociates ? (count / totalAssociates) * 100 : 0,
            colorClass: index === 0 ? 'muted' : 'primary',
            animDelay: index * 80,
            responseDetails: Array.isArray(row?.dealer_breakdown ?? row?.dealerBreakdown)
                ? (row?.dealer_breakdown ?? row?.dealerBreakdown)
                : [],
        };
    }).filter((bar) => bar.label);
};

export const transformAssociateByAreaAndState = (rows: any[]): any[] => {
    if (!Array.isArray(rows)) {
        return [];
    }

    return rows.map((areaRow) => {
        const stateBreakdownSource = areaRow?.state_breakdown ?? areaRow?.stateBreakdown;
        const stateBreakdown = Array.isArray(stateBreakdownSource)
            ? stateBreakdownSource.map((dealerRow: any) => ({
                ...dealerRow,
                dealerCode: `${dealerRow?.dealer_code ?? dealerRow?.dealerCode ?? ''}`,
                dealerName: dealerRow?.dealer_name ?? dealerRow?.dealerName ?? '',
                state: dealerRow?.state ?? dealerRow?.stateCode ?? '',
                associateCount: Number(dealerRow?.associate_count ?? dealerRow?.associateCount ?? 0),
                certifiable: Number(dealerRow?.certifiable_count ?? dealerRow?.certifiableCount ?? 0),
                nonCertifiable: Number(dealerRow?.non_certifiable_count ?? dealerRow?.nonCertifiableCount ?? 0),
                certified: Number(dealerRow?.certified_count ?? dealerRow?.certifiedCount ?? 0),
                nonCertified: Number(dealerRow?.non_certified_count ?? dealerRow?.nonCertifiedCount ?? 0),
                master: Number(dealerRow?.master ?? 0),
                senior: Number(dealerRow?.senior ?? 0),
                foundation: Number(dealerRow?.foundation ?? 0),
            }))
            : [];

        return {
            ...areaRow,
            name: areaRow?.area ?? areaRow?.name ?? '',
            region: areaRow?.area ?? areaRow?.region ?? '',
            totalAssociates: Number(areaRow?.total_associates ?? areaRow?.totalAssociates ?? 0),
            stateBreakdown,
        };
    }).filter((areaRow) => areaRow.name);
};

export const DealerPerformanceDashboardGridSchema: Array<dynamicComponentSchema> = [
    {
        component: FormContainerComponent,
        type: SECTION_TYPE['Filter'],
        inputs: {
            elementId: DealerScorecardSectionNames['DashboardDealerSelector'],
            dynamicFormArray: DealerPerformanceDashboardDealerFilterSchema,
            formForFilter: true,
        },
    },
    {
        component: DynamicCardsComponent,
        type: SECTION_TYPE['Dashboard'],
        inputs: {
            elementId: DealerScorecardSectionNames['DashboardCards'],
            schema: DealerPerformanceDashboardCardsSchema,
            columns: 6,
            rows: 1,
            minCardWidth: '0',
            format: 'dealerPerformanceCard',
            apiConfig: ScorecardDashboardSummarySearchConfig,
            filterSchema: {},
            globalFilterFields: [DealerPerformanceDashboardFilterTrigger],
            requiredGlobalPayloadFields: DealerPerformanceDashboardRequiredPayloadFields,
            onLoadFetch: false,
            defaultLoader: true,
        },
    },
    {
        component: ChartComponent,
        type: SECTION_TYPE['Chart'],
        inputs: {
            chartSchema: DealerPerformanceTopModelsBarChart,
            chartType: CHART_TYPE['BarChart'],
            elementId: DealerScorecardSectionNames['DashboardTopModelsChart'],
            apiConfig: ScorecardTopModelsBySalesSearchConfig,
            filterSchema: {},
            globalFilterFields: [DealerPerformanceDashboardFilterTrigger],
            requiredGlobalPayloadFields: DealerPerformanceDashboardRequiredPayloadFields,
            exportConfig: {
                ...DealerPerformanceTopModelsBarChart['exportConfig'],
                exportMessage: 'Top Models By Sales Data',
            },
            infoFields: structuredClone([{
                ...TotalAmountText, fieldLabel:TOTAL_SALES, calculateBy: 'sales'
            }]),
            onLoadFetch: false,
            defaultLoader: true,
        },
    },
    {
        component: ChartComponent,
        type: SECTION_TYPE['Chart'],
        inputs: {
            chartSchema: DealerPerformanceSalesRevenueTrendChart,
            chartType: CHART_TYPE['ColumnChart'],
            elementId: DealerScorecardSectionNames['DashboardSalesTrendChart'],
            apiConfig: ScorecardSalesTrendPerMonthSearchConfig,
            filterSchema: {},
            globalFilterFields: [DealerPerformanceDashboardFilterTrigger],
            requiredGlobalPayloadFields: DealerPerformanceDashboardRequiredPayloadFields,
            exportConfig: {
                ...DealerPerformanceSalesRevenueTrendChart['exportConfig'],
                exportMessage: 'Sales Trend Data',
            },
            infoFields: structuredClone([{
                ...TotalAmountText, fieldLabel:TOTAL_SALES,calculateBy: 'sales'
            }]),
            onLoadFetch: false,
            defaultLoader: true,
        },
    },
    // {
    //     component: ChartComponent,
    //     type: SECTION_TYPE['Chart'],
    //     inputs: {
    //         chartSchema: DealerPerformanceCertificationDonutChart,
    //         chartType: CHART_TYPE['PieChart'],
    //         elementId: DealerScorecardSectionNames['DashboardRoleMixChart'],
    //         apiConfig: ScorecardCertificationSummarySearchConfig,
    //         filterSchema: {},
    //         globalFilterFields: [DealerPerformanceDashboardFilterTrigger],
    //         requiredGlobalPayloadFields: DealerPerformanceDashboardRequiredPayloadFields,
    //         dataTransform: transformScorecardCertificationSummary,
    //         exportConfig: {
    //             ...DealerPerformanceCertificationDonutChart['exportConfig'],
    //             exportMessage: 'Dealer Performance Certification Chart Data',
    //         },
    //         onLoadFetch: false,
    //         defaultLoader: true,
    //     },
    // }
];
export const DealerScorecardChartsGridSchema: Array<dynamicComponentSchema> = [

    {
        component: FormContainerComponent,
        type: SECTION_TYPE['GlobalFilter'],
        inputs: {
            dynamicFormArray: {
                formName: 'dealerScorecardGlobalFilter',
                FieldsArray: structuredClone([{ ...structuredClone(AreaDropdown), multiple: false }]),
                searchConfig: { method: 'POST', path: '', body: { region: '' } },
            },
        }
    },
    {
        component: DynamicCardsComponent,
        type: SECTION_TYPE['Dashboard'],
        inputs: {
            elementId: DealerScorecardSectionNames['SummaryCards'],
            schema: DealerScorecardCardsSchema,
            columns: 4,
            rows: 1,
            format: 'borderCard',
            apiConfig: ScorecardDashboardSummarySearchConfig,
            filterSchema: {},
            globalFilterFields: [DealerOverviewFilterTrigger],
            onLoadFetch: false,
            defaultLoader: true,
        },
    },
    {
        component: FilterListContainerComponent,
        type: SECTION_TYPE['List'],
        inputs: {
            elementId: DealerScorecardSectionNames['DealerList'],
            DataTableSchema: DealerScorecardTableSchema,
            apiConfig: DealerBreakdownSearchConfig,
            forSection: true,
            filterSchema: { FieldsArray: [] } as DynamicFormArray,
            globalFilterFields: [DealerOverviewFilterTrigger],
            onLoadFetch: false,
        },
    },
    {
        component: DynamicProgressBarComponent,
        type: SECTION_TYPE['Dashboard'],
        inputs: {
            elementId: DealerScorecardSectionNames['CertificationProgress'],
            title: 'Certification Status',
            apiConfig: CertificationStatusDealerBreakdownSearchConfig,
            dataTransform: transformCertificationStatusProgressBars,
            desktopColumns: 4,
            viewMode: 'grid',
            globalFilterFields: [DealerOverviewFilterTrigger],
            onLoadFetch: false,
        },
    },
    {
        component: ChartComponent,
        type: SECTION_TYPE['Chart'],
        inputs: {
            chartSchema: DealerCertificationPieChart,
            chartType: CHART_TYPE['PieChart'],
            elementId: DealerScorecardSectionNames['CertificationChart'],
            apiConfig: CertificationStatusDealerBreakdownSearchConfig,
            filterSchema: { FieldsArray: [] } as DynamicFormArray,
            globalFilterFields: [DealerOverviewFilterTrigger],
            exportConfig: {
                ...DealerCertificationPieChart['exportConfig'],
                exportMessage: 'Certification Status Data',
            },
            infoFields: structuredClone([{
                ...TotalCountText, fieldLabel: TOTAL_COUNT, calculateBy: 'total_associate_count'
            }]),
            onLoadFetch: false,
            defaultLoader: true,
        },
    },
    {
        component: DsMapDrilldownComponent,
        type: SECTION_TYPE['Dashboard'],
        inputs: {
            chartSchema: DealerAssociatesChart,
            elementId: DealerScorecardSectionNames['AssociatesChart'],
            dataInput: [],
            apiConfig: AssociateByAreaAndStateSearchConfig,
            dataTransform: transformAssociateByAreaAndState,
            globalFilterFields: [DealerOverviewFilterTrigger],
            infoFields: structuredClone([{
                ...TotalCountText, fieldLabel: TOTAL_COUNT, calculateBy: 'totalAssociates'
            }]),
            exportConfig: {
                ...DealerAssociatesChart['exportConfig'],
                exportMessage: 'Dealer Associates Chart Data',
            },
            onLoadFetch: false,
        },
    },
    {
        component: ChartComponent,
        type: SECTION_TYPE['Chart'],
        inputs: {
            chartSchema: DealerRoleMixChart,
            chartType: CHART_TYPE['DonutChart'],
            elementId: DealerScorecardSectionNames['RoleMixChart'],
            apiConfig: CertifiableRoleAssociatesSearchConfig,
            filterSchema: { FieldsArray: [] } as DynamicFormArray,
            globalFilterFields: [DealerOverviewFilterTrigger],
            exportConfig: {
                ...DealerRoleMixChart['exportConfig'],
                exportMessage: 'Dealer Role Mix Chart Data',
            },
            infoFields: structuredClone([{
                ...TotalCountText, fieldLabel: TOTAL_COUNT, calculateBy: 'total_associate_count'
            }]),
            onLoadFetch: false,
            defaultLoader: true,
        },
    }
];

export const DealerScorecardReportsGridSchema: Array<dynamicComponentSchema> = [
    {
        component: FormContainerComponent,
        type: SECTION_TYPE['GlobalFilter'],
        inputs: {
            dynamicFormArray: {
                formName: 'salesOverviewGlobalFilter',
                FieldsArray: structuredClone([{ ...structuredClone(AreaDropdown), multiple: false }, {...structuredClone(CalendarYearDropdown),displayName:'Year', options: dealerYearOptions}]),
                searchConfig: { method: 'POST', path: '', body: { region: '' } },
            },
        }
    },
    {
        component: DynamicCardsComponent,
        type: SECTION_TYPE['Dashboard'],
        inputs: {
            elementId: DealerScorecardSectionNames['SalesOverviewCards'],
            schema: [
                { label: TOTAL_SALES_LABEL, subLabel: 'All dealers combined', fieldKey: 'total_sales' },
                { label: 'Average Sales per Dealer', subLabel: 'Mean across network', fieldKey: 'average_sales_per_dealer' },
                { label: TOP_DEALER_LABEL, subLabel: 'Highest performer', fieldKey: 'top_dealer_sales' },
                { label: 'Dealer Count', subLabel: 'Matching dealers', fieldKey: 'dealer_count' },
            ],
            columns: 4,
            rows: 1,
            format: 'borderCard',
            apiConfig: SalesOverviewCardsSearchConfig,
            filterSchema: {},
            globalFilterFields: [SalesOverviewFilterTrigger],
            onLoadFetch: false,
            defaultLoader: true,
        },
    }, {
        component: ChartComponent,
        type: SECTION_TYPE['Chart'],
        inputs: {
            chartSchema: SalesByDealerChart,
            chartType: CHART_TYPE['ColumnChart'],
            elementId: DealerScorecardSectionNames['SalesByDealerChart'],
            apiConfig: TotalSalesByAreaSearchConfig,
            filterSchema: {},
            globalFilterFields: [SalesOverviewFilterTrigger],
            exportConfig: {
                ...SalesByDealerChart['exportConfig'],
                exportMessage: 'Sales By Area Chart Data',
            },
            infoFields: structuredClone([{
                ...TotalAmountText, fieldLabel:TOTAL_SALES, calculateBy: 'total_sales'
            }]),
            onLoadFetch: false,
            defaultLoader: true,
        },
    },
    {
        component: ChartComponent,
        type: SECTION_TYPE['Chart'],
        inputs: {
            chartSchema: Top10DealersChart,
            chartType: CHART_TYPE['ColumnChart'],
            elementId: DealerScorecardSectionNames['Top10DealersChart'],
            apiConfig: TopTenDealersBySalesSearchConfig,
            filterSchema: {},
            globalFilterFields: [SalesOverviewFilterTrigger],
            exportConfig: {
                ...Top10DealersChart['exportConfig'],
                exportMessage: 'Top 10 Dealers Chart Data',
            },
            infoFields: structuredClone([{
                ...TotalAmountText, fieldLabel:TOTAL_SALES, calculateBy: 'sales'
            }]),
            onLoadFetch: false,
            defaultLoader: true,
        },
    },
    {
        component: ChartComponent,
        type: SECTION_TYPE['Chart'],
        inputs: {
            chartSchema: SalesTrendAreasplineChart,
            chartType: CHART_TYPE['AreasplineChart'],
            elementId: DealerScorecardSectionNames['SalesTrendAreasplineChart'],
            apiConfig: SalesTrendsAcrossAllDealersSearchConfig,
            filterSchema: {},
            globalFilterFields: [SalesOverviewFilterTrigger],
            exportConfig: {
                ...SalesTrendAreasplineChart['exportConfig'],
                exportMessage: 'Sales Trend Chart Data',
            },
            infoFields: structuredClone([{
                ...TotalAmountText, fieldLabel:TOTAL_SALES, calculateBy: 'sales'
            }]),
            onLoadFetch: false,
            defaultLoader: true,
        },
    },
    {
        component: FilterListContainerComponent,
        type: SECTION_TYPE['List'],
        inputs: {
            elementId: 'salesOverviewData',
            DataTableSchema: SalesOverviewTableSchema,
            props: { isTabStretch: true },
            forSection: true,
            filterSchema: SalesOverviewFilterSchema,
            apiConfig: SalesDataBeforeAfterEventSearchConfig,
            globalFilterFields: [SalesOverviewFilterTrigger],
            onLoadFetch: false,
        },
    }
];

export const DealerScorecardDashboardTabConfig: Array<TabConfig> = [
    {
        label: 'Dashboard',
        tabId: 'dealerPerformanceDashboard',
        type: SECTION_TYPE.Dashboard,
        component: DashboardContainerComponent,
        inputs: { gridSchema: DealerPerformanceDashboardGridSchema },
    },
    {
        label: 'Dealers Overview',
        tabId: 'dealerScorecardCharts',
        type: SECTION_TYPE.Dashboard,
        component: DashboardContainerComponent,
        inputs: { gridSchema: DealerScorecardChartsGridSchema },
    },
    {
        label: 'Sales Overview',
        tabId: 'dealerScorecardReports',
        type: SECTION_TYPE.Dashboard,
        component: DashboardContainerComponent,
        inputs: { gridSchema: DealerScorecardReportsGridSchema },
    },
];
